import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FaEye, FaEyeSlash, FaLock, FaRegUser } from "react-icons/fa";
import {useAuth} from '../../../context/AuthContext.js';
import { useLanguage } from '../../../context/LanguageContext.js';
import ButtonWithLoader from "../../../components/Button/ButtonLoader/ButtonLoader.js";
import GoogleButton from '../GoogleButton/GoogleButton.jsx';
import { authAPI } from '../../../api/api.js';
import AuthBrand from '../AuthBrand/AuthBrand.jsx';
import { getGoogleAuthRedirectError } from '../../../Utils/authErrors.js';

function Register({navigate}) {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [error, setError] = useState('');
    const [passwordVisible, setPasswordVisible] = useState(false);

    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const { t } = useLanguage();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const urlError = getGoogleAuthRedirectError(searchParams, t);
        if (urlError) {
            setError(urlError);
        }
    }, [searchParams, t]);

    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const result = await register({
                email: formData.email,
                password: formData.password,
            });

            const params = new URLSearchParams(window.location.search);
            const selectedPlan = params.get('plan');
            const selectedInterval = params.get('interval') === 'yearly' ? 'yearly' : 'monthly';

            if (result.requiresEmailVerification) {
                const email = result.email || formData.email;
                const verifyParams = new URLSearchParams();
                if (email) {
                    verifyParams.set('email', email);
                }
                if (selectedPlan) {
                    verifyParams.set('plan', selectedPlan);
                }
                verifyParams.set('interval', selectedInterval);

                navigate(`/verify-email-code?${verifyParams.toString()}`, {
                    state: {
                        email,
                        maskedEmail: result.maskedEmail,
                        plan: selectedPlan,
                        interval: selectedInterval,
                        codeSent: true,
                    },
                });
                return;
            }

            const { resolvePostLoginPath } = await import('../../../Utils/postLoginNavigation.js');
            const defaultPath = await resolvePostLoginPath({
                passwordChangeRequired: result.requiresPasswordChange,
            });

            if (result.requiresPasswordChange) {
                navigate(defaultPath);
                return;
            }

            const billingParams = new URLSearchParams({ interval: selectedInterval });
            if (selectedPlan) {
                billingParams.set('plan', selectedPlan);
            }
            navigate(`/select-plan?${billingParams.toString()}`);
        } catch (err) {
            const data = err.response?.data;
            setError(
                (typeof data?.message === 'string' && data.message.trim()) ||
                    data?.errors?.[0]?.msg ||
                    (data?.message?.match(/already exists/i)
                        ? t('auth.register.emailExists')
                        : null) ||
                    err.message ||
                    t('auth.register.failed'),
            );
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleAuth = () => {
        const params = new URLSearchParams(window.location.search);
        const selectedPlan = params.get('plan');
        const selectedInterval = params.get('interval') === 'yearly' ? 'yearly' : 'monthly';
        window.location.href = authAPI.googleAuth({
            intent: 'register',
            plan: selectedPlan || undefined,
            interval: selectedInterval,
        });
    };


    return (
      <main className="auth-page">
        <AuthBrand corner to="/login" />
        <div className="auth-shell container">
          <form className="auth-card" onSubmit={handleSubmit}>
            <h1 className="auth-title">{t('auth.register.eyebrow')}</h1>

            <GoogleButton onClick={handleGoogleAuth} />

            <div className="auth-fields">
              <div className="auth-field">
                <FaRegUser className="auth-field-icon" />
                <div className="auth-field-control">
                  <input
                    placeholder=""
                    name="email"
                    type="email"
                    required
                    className="auth-input"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  <label className="auth-input-label">{t('auth.fields.email')}</label>
                </div>
              </div>

              <div className="auth-field">
                <FaLock className="auth-field-icon" />
                <div className="auth-field-control">
                  <input
                    placeholder=""
                    name="password"
                    type={passwordVisible ? 'text' : 'password'}
                    required
                    className="auth-input"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <label className="auth-input-label">{t('auth.fields.password')}</label>
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    aria-label={
                      passwordVisible
                        ? t('auth.fields.hidePassword')
                        : t('auth.fields.showPassword')
                    }
                    className="auth-password-toggle"
                  >
                    {passwordVisible ? (
                      <FaEyeSlash className="auth-password-icon" />
                    ) : (
                      <FaEye className="auth-password-icon" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="auth-action-box">
              <ButtonWithLoader text={t('auth.register.submit')} loading={loading} type="submit" />
            </div>

            <p className="auth-link-row">
              {t('auth.register.hasAccount')}{' '}
              <strong className="auth-link" onClick={() => navigate('/login')}>
                {t('auth.register.login')}
              </strong>
            </p>

            {error && <p className="auth-message auth-message--error">{error}</p>}
          </form>
        </div>
      </main>
    );
}

export default Register;
